"use client";

import Image from "next/image";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { getSessionAction } from "@/actions/serverActions/auth/getSessionAction";
import aiAgentImg from "@/assets/images/AI-Agent.png";
import BellIcon from "@/assets/images/notification-01.svg";
import AppsIcon from "@/assets/images/SquaresFour.svg";
import { AskAiPopover } from "@/components/ai-agent/AskAiPopover";
import { NotificationPanel } from "./NotificationPanel";
import { ProfileMenu } from "./ProfileMenu";

/** Which top-nav dropdown is open (only one at a time). */
type OpenMenu = "ask" | "notif" | null;

function NavSeparator() {
  return <div className="h-5 w-px bg-neutral-200" />;
}

/** Ask AI opens the assistant popover; the bell opens the notifications panel. */
export function TopNavActions() {
  const [openMenu, setOpenMenu] = useState<OpenMenu>(null);
  const [firstName, setFirstName] = useState("there");

  // Greeting name for the popover — read-only session lookup, like ProfileMenu.
  useEffect(() => {
    getSessionAction()
      .then((session) => {
        if (session?.user?.first_name) {
          setFirstName(session.user.first_name.trim());
        }
      })
      .catch(() => {});
  }, []);

  const toggle = (menu: Exclude<OpenMenu, null>) =>
    setOpenMenu((m) => (m === menu ? null : menu));
  const close = () => setOpenMenu(null);

  // The sidebar "Cashless Ai" item already opens the full agent page; disable the
  // header launcher there so it isn't a redundant (and confusing) second entry.
  const pathname = usePathname();
  const onAiAgentPage = pathname?.startsWith("/ai-agent") ?? false;

  return (
    <div className="flex items-center gap-3">
      {/* Ask AI — gradient-border pill that toggles the assistant popover. */}
      <div className="relative">
        <button
          type="button"
          aria-label="Ask AI"
          aria-expanded={openMenu === "ask"}
          disabled={onAiAgentPage}
          onClick={() => toggle("ask")}
          className={`paragraph-sm flex items-center justify-center gap-1 rounded-full border-[1.5px] border-transparent py-1 pl-2 pr-3 font-medium text-neutral-800 [background:linear-gradient(#fff,#fff)_padding-box,linear-gradient(90deg,#3859ff,#e8e4ff,#9747ff)_border-box] ${
            onAiAgentPage ? "cursor-not-allowed opacity-50" : "cursor-pointer"
          }`}
        >
          <Image
            src={aiAgentImg}
            alt=""
            width={16}
            height={16}
            className="shrink-0"
          />
          Ask AI
        </button>
        {openMenu === "ask" && !onAiAgentPage && (
          <div className="absolute top-full right-0 z-50 mt-2">
            <AskAiPopover name={firstName} onClose={close} />
          </div>
        )}
      </div>

      <NavSeparator />

      {/* Apps switcher — inert */}
      <button
        type="button"
        aria-label="Switch app (coming soon)"
        className="flex size-9 cursor-default items-center justify-center rounded-md text-neutral-500 hover:bg-neutral-100"
      >
        <AppsIcon className="size-6" />
      </button>

      <NavSeparator />

      {/* Notifications — toggles the notifications panel. */}
      <div className="relative">
        <button
          type="button"
          aria-label="Notifications"
          aria-expanded={openMenu === "notif"}
          onClick={() => toggle("notif")}
          className="relative flex size-9 cursor-pointer items-center justify-center rounded-md text-neutral-500 hover:bg-neutral-100"
        >
          <BellIcon className="size-6" />
          <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-error-500" />
        </button>
        {openMenu === "notif" && (
          <div className="absolute top-full right-0 z-50 mt-2">
            <NotificationPanel onMarkAllRead={close} />
          </div>
        )}
      </div>

      <NavSeparator />

      <ProfileMenu />

      {/* Click-away layer shared by both dropdowns. */}
      {openMenu && (
        <button
          type="button"
          aria-label="Close menu"
          onClick={close}
          className="fixed inset-0 z-40 cursor-default"
        />
      )}
    </div>
  );
}
